import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";
import { CustomEventType } from "@/enum/custom-event.enum";
import { ScrapeLevel } from "@/enum/job.enum";
import { ListSourceType } from "@/enum/list.enum";
import { LiPostType } from "@/enum/liprofile.enum";
import { LiEventType } from "@/enum/lievent.enum";
import { LiPeopleType } from "@/enum/li-people.enum";
import { LiSnType } from "@/enum/li-sn.enum";
import { ParallelAiType } from "@/enum/parallel-ai-type.enum";
import { FundingMaType } from "@/enum/funding-ma.enum";
import { CompanyEnrichType } from "@/enum/company-enrich-type.enum";

import { CategoryTile, SourceMode, Tile } from "./tileConfig";

export enum CatalogTileRecurrence {
	ONE_TIME = "ONE_TIME",
	RECURRING = "RECURRING",
}

export enum CatalogTileRequirement {
	LINKEDIN_ACCOUNT = "LINKEDIN_ACCOUNT",
	SALES_NAVIGATOR = "SALES_NAVIGATOR",
	COMPANY_ADMIN = "COMPANY_ADMIN",
	WEBSITE_SCRAPE_CREDITS = "WEBSITE_SCRAPE_CREDITS",
	TRACKER_AS_LIST_FEATURE = "TRACKER_AS_LIST_FEATURE",
	SMART_LIST_FEATURE = "SMART_LIST_FEATURE",
}

export enum CatalogCategory {
	UPLOAD = "UPLOAD",
	SCRAPING = "SCRAPING",
	SIGNALS = "SIGNALS",
	LINKEDIN = "LINKEDIN",
	DATABASE = "DATABASE",
	JOBS = "JOBS",
	FUNDING = "FUNDING",
	REENGAGE = "REENGAGE",
	EMPTY = "EMPTY",
}

export enum CatalogIconVariant {
	CSV = "CSV",
	SCRAPE = "SCRAPE",
	LINKEDIN = "LINKEDIN",
	DATABASE = "DATABASE",
	MAPS = "MAPS",
	JOBS = "JOBS",
	FUNDING = "FUNDING",
	SEMANTIC = "SEMANTIC",
	TRACKER = "TRACKER",
	EMPTY = "EMPTY",
}

export interface CatalogTile extends Tile {
	id: string;
	recurrences: CatalogTileRecurrence[];
	keywords: string;
	requirements: CatalogTileRequirement[];
	iconVariant: CatalogIconVariant;
	categories: CatalogCategory[];
}

type TileSubType = CsvEnrichmentType | CustomEventType | ScrapeLevel | undefined;

interface CatalogTileOverride {
	recurrences: CatalogTileRecurrence[];
	keywords: string;
	requirements?: CatalogTileRequirement[];
	iconVariant: CatalogIconVariant;
	categories: CatalogCategory[];
}

export const getCatalogTileId = (
	listType: ListSourceType,
	subType?: TileSubType,
	mode?: SourceMode
): string => `${listType}-${[subType, mode].filter(Boolean).join("-") || "DEFAULT"}`;

const oneTimeOnly = [CatalogTileRecurrence.ONE_TIME];
const oneTimeAndRecurring = [CatalogTileRecurrence.ONE_TIME, CatalogTileRecurrence.RECURRING];

const catalogTileOverrides: Record<string, CatalogTileOverride> = {
	[getCatalogTileId(ListSourceType.CSV_ENRICHMENTS, CsvEnrichmentType.PERSON)]: {
		recurrences: oneTimeOnly,
		keywords:
			"upload xlsx file people csv import names emails linkedin urls attendees event participants crm contacts crm leads newsletter signups trade show conference events",
		iconVariant: CatalogIconVariant.CSV,
		categories: [CatalogCategory.UPLOAD],
	},
	[getCatalogTileId(ListSourceType.CSV_ENRICHMENTS, CsvEnrichmentType.ORGANIZATION)]: {
		recurrences: oneTimeOnly,
		keywords:
			"upload xlsx file companies csv import domains organizations decision makers exhibitors target accounts accounts conference trade show events",
		iconVariant: CatalogIconVariant.CSV,
		categories: [CatalogCategory.UPLOAD],
	},
	[getCatalogTileId(ListSourceType.CSV_ENRICHMENTS, CsvEnrichmentType.PERSON_BY_TITLE)]: {
		recurrences: oneTimeOnly,
		keywords:
			"upload xlsx job titles companies find people by title event attendees trade show conference events",
		iconVariant: CatalogIconVariant.CSV,
		categories: [CatalogCategory.UPLOAD],
	},
	[getCatalogTileId(ListSourceType.CUSTOM_EVENTS, CustomEventType.PERSON)]: {
		recurrences: oneTimeOnly,
		keywords:
			"scraping service event attendees people networking app participant list matchmaking participants trade show conference login mobile app events",
		requirements: [CatalogTileRequirement.WEBSITE_SCRAPE_CREDITS],
		iconVariant: CatalogIconVariant.SCRAPE,
		categories: [CatalogCategory.SCRAPING],
	},
	[getCatalogTileId(ListSourceType.CUSTOM_EVENTS, CustomEventType.ORGANIZATION)]: {
		recurrences: oneTimeOnly,
		keywords:
			"scraping service exhibitors companies sponsors trade show website accounts trade shows conferences events login",
		requirements: [CatalogTileRequirement.WEBSITE_SCRAPE_CREDITS],
		iconVariant: CatalogIconVariant.SCRAPE,
		categories: [CatalogCategory.SCRAPING],
	},
	[getCatalogTileId(ListSourceType.LI_PROFILE_VISITS)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "linkedin profile visits visitors premium recurring signal profile visitors",
		requirements: [CatalogTileRequirement.LINKEDIN_ACCOUNT],
		iconVariant: CatalogIconVariant.LINKEDIN,
		categories: [CatalogCategory.LINKEDIN, CatalogCategory.SIGNALS],
	},
	[getCatalogTileId(ListSourceType.LI_COMPANY_ACTIVITY)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "linkedin company followers company page admin recurring linkedin company page",
		requirements: [CatalogTileRequirement.COMPANY_ADMIN],
		iconVariant: CatalogIconVariant.LINKEDIN,
		categories: [CatalogCategory.LINKEDIN, CatalogCategory.SIGNALS],
	},
	[getCatalogTileId(ListSourceType.LI_EVENTS, undefined, LiEventType.SPECIFIC)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"linkedin specific event attendees webinar event url going rsvp conference trade show single event",
		requirements: [CatalogTileRequirement.LINKEDIN_ACCOUNT],
		iconVariant: CatalogIconVariant.LINKEDIN,
		categories: [CatalogCategory.LINKEDIN],
	},
	[getCatalogTileId(ListSourceType.LI_POST, undefined, LiPostType.BY_TOPIC)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"linkedin posts topic keyword hashtag boolean thought leadership thought leaders competitors monitor posting about author",
		requirements: [CatalogTileRequirement.LINKEDIN_ACCOUNT],
		iconVariant: CatalogIconVariant.LINKEDIN,
		categories: [CatalogCategory.LINKEDIN],
	},
	[getCatalogTileId(ListSourceType.LI_POST, undefined, LiPostType.BY_URL)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"linkedin specific post url engagement likes comments likers commenters who liked who commented single post",
		requirements: [CatalogTileRequirement.LINKEDIN_ACCOUNT],
		iconVariant: CatalogIconVariant.LINKEDIN,
		categories: [CatalogCategory.LINKEDIN],
	},
	[getCatalogTileId(ListSourceType.LI_POST, undefined, LiPostType.BY_PROFILE)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"linkedin profile posts engagement track person company feed monitor recent posts likes comments followers",
		requirements: [CatalogTileRequirement.LINKEDIN_ACCOUNT],
		iconVariant: CatalogIconVariant.LINKEDIN,
		categories: [CatalogCategory.LINKEDIN],
	},
	[getCatalogTileId(ListSourceType.COMPANY_ENRICH, undefined, CompanyEnrichType.SEARCH)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"lead database contacts firmographic technographic persona filters classic search leads industry company size hq",
		iconVariant: CatalogIconVariant.DATABASE,
		categories: [CatalogCategory.DATABASE],
	},
	[getCatalogTileId(ListSourceType.COMPANY_ENRICH, undefined, CompanyEnrichType.LOOK_A_LIKE)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"lead database lookalike look a like similar companies seed examples find similar ideal customer accounts",
		iconVariant: CatalogIconVariant.DATABASE,
		categories: [CatalogCategory.DATABASE],
	},
	[getCatalogTileId(ListSourceType.COMPANY_ENRICH, undefined, CompanyEnrichType.NEW_IN_ROLE)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"lead database new in role new job new employer changed role champions promotion job change time window",
		iconVariant: CatalogIconVariant.DATABASE,
		categories: [CatalogCategory.JOBS],
	},
	[getCatalogTileId(ListSourceType.LI_PEOPLE, undefined, LiPeopleType.SPECIFIC)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "linkedin people import profile url paste urls specific profiles links",
		requirements: [CatalogTileRequirement.LINKEDIN_ACCOUNT],
		iconVariant: CatalogIconVariant.LINKEDIN,
		categories: [CatalogCategory.LINKEDIN, CatalogCategory.DATABASE],
	},
	[getCatalogTileId(ListSourceType.LI_SN, undefined, LiSnType.SPECIFIC)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "linkedin sales navigator import url lead list saved search by url navigator list",
		requirements: [CatalogTileRequirement.SALES_NAVIGATOR],
		iconVariant: CatalogIconVariant.LINKEDIN,
		categories: [CatalogCategory.LINKEDIN, CatalogCategory.DATABASE],
	},
	[getCatalogTileId(ListSourceType.MAPS, ScrapeLevel.GET_ENRICHMENT)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "google maps local business location category enrichment",
		iconVariant: CatalogIconVariant.MAPS,
		categories: [CatalogCategory.DATABASE],
	},
	[getCatalogTileId(ListSourceType.FUNDING_ROUNDS, undefined, FundingMaType.FUNDING)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"funding rounds startups raised capital startup fundraising vc seed series investment round",
		iconVariant: CatalogIconVariant.FUNDING,
		categories: [CatalogCategory.FUNDING],
	},
	[getCatalogTileId(ListSourceType.FUNDING_ROUNDS, undefined, FundingMaType.MA)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "mergers acquisitions m&a acquired merger acquisition takeover deal bought",
		iconVariant: CatalogIconVariant.FUNDING,
		categories: [CatalogCategory.FUNDING],
	},
	[getCatalogTileId(ListSourceType.PARALLEL_AI, ScrapeLevel.GET_ENRICHMENT, ParallelAiType.SEARCH)]:
		{
			recurrences: oneTimeAndRecurring,
			keywords:
				"ai prompt semantic search natural language companies ideal customer describe target accounts",
			iconVariant: CatalogIconVariant.SEMANTIC,
			categories: [CatalogCategory.DATABASE, CatalogCategory.SIGNALS],
		},
	[getCatalogTileId(
		ListSourceType.PARALLEL_AI,
		ScrapeLevel.GET_ENRICHMENT,
		ParallelAiType.MONITOR
	)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"ai monitor news events signals web product launch layoff expansion announcements watch ongoing",
		iconVariant: CatalogIconVariant.SEMANTIC,
		categories: [CatalogCategory.DATABASE, CatalogCategory.SIGNALS],
	},
	[getCatalogTileId(ListSourceType.JOBS, ScrapeLevel.GET_ENRICHMENT)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "jobs hiring companies open roles recruiting job portals job",
		iconVariant: CatalogIconVariant.JOBS,
		categories: [CatalogCategory.JOBS],
	},
	[getCatalogTileId(ListSourceType.TRACKER, CsvEnrichmentType.PERSON)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "profile tracker custom signal target accounts abm monitoring signals clients leads",
		requirements: [CatalogTileRequirement.TRACKER_AS_LIST_FEATURE],
		iconVariant: CatalogIconVariant.TRACKER,
		categories: [CatalogCategory.SIGNALS],
	},
	[getCatalogTileId(ListSourceType.JOB_CHANGE, CsvEnrichmentType.PERSON)]: {
		recurrences: oneTimeAndRecurring,
		keywords:
			"job change tracking champions new role monitor people champion new in role new job new company change",
		requirements: [CatalogTileRequirement.TRACKER_AS_LIST_FEATURE],
		iconVariant: CatalogIconVariant.JOBS,
		categories: [CatalogCategory.JOBS, CatalogCategory.SIGNALS],
	},
	[getCatalogTileId(ListSourceType.SMART_LIST)]: {
		recurrences: oneTimeAndRecurring,
		keywords: "contacts engage re-engage re engage nurturing smart list existing contacts campaign",
		requirements: [CatalogTileRequirement.SMART_LIST_FEATURE],
		iconVariant: CatalogIconVariant.DATABASE,
		categories: [CatalogCategory.REENGAGE],
	},
	[getCatalogTileId(ListSourceType.EMPTY, ScrapeLevel.GET_ENRICHMENT)]: {
		recurrences: oneTimeOnly,
		keywords: "empty manual blank scratch add leads new campaign",
		iconVariant: CatalogIconVariant.EMPTY,
		categories: [CatalogCategory.EMPTY],
	},
	[getCatalogTileId(ListSourceType.EMPTY)]: {
		recurrences: oneTimeOnly,
		keywords: "empty manual blank scratch add leads new campaign",
		iconVariant: CatalogIconVariant.EMPTY,
		categories: [CatalogCategory.EMPTY],
	},
};

const getInheritedRequirements = (requires?: string): CatalogTileRequirement[] => {
	if (requires === "TRACKER_AS_LIST") {
		return [CatalogTileRequirement.TRACKER_AS_LIST_FEATURE];
	}

	if (requires === "SMART_LIST") {
		return [CatalogTileRequirement.SMART_LIST_FEATURE];
	}

	return [];
};

export const getCatalogTiles = (categories: CategoryTile[]): CatalogTile[] =>
	categories.flatMap((category) =>
		category.items.map((item) => {
			const id = getCatalogTileId(item.listType, item.subType, item.mode);
			const override = catalogTileOverrides[id];
			const inheritedRequirements = getInheritedRequirements(category.requires);

			return {
				...item,
				id,
				recurrences: override?.recurrences ?? oneTimeAndRecurring,
				keywords: override?.keywords ?? `${item.title} ${item.description}`,
				requirements: [...inheritedRequirements, ...(override?.requirements ?? [])],
				iconVariant: override?.iconVariant ?? CatalogIconVariant.EMPTY,
				categories: override?.categories ?? [],
			};
		})
	);
